import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  getBytes,
  deleteObject,
} from '@angular/fire/storage';
import {
  Functions,
  httpsCallable,
  HttpsCallableResult,
} from '@angular/fire/functions';
import {
  EnvironmentInjector,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import { SaltService } from '../salt/salt.service';
import { Observable, from, map, of, switchMap, tap, catchError } from 'rxjs';
import { FileMetadata } from '../../models/file-metadata.model';
import { ExcelCacheService } from '../excel-cache/excel-cache.service';

/**
 * This service connects with the firebase API to handle server data (excel files and metadata)
 */
@Injectable({
  providedIn: 'root',
})
export class FileService {
  private _registerExcelFile: ReturnType<typeof httpsCallable>;
  private _getExcelFiles: ReturnType<typeof httpsCallable>;
  private _getExcelFileById: ReturnType<typeof httpsCallable>;
  private _deleteExcelFile: ReturnType<typeof httpsCallable>;
  private _modifyExcelFile: ReturnType<typeof httpsCallable>;

  private fileMap = new Map<string, Blob>();

  constructor(
    private _storage: Storage,
    private _functions: Functions,
    private _saltService: SaltService,
    private _injector: EnvironmentInjector,
    private _excelCache: ExcelCacheService
  ) {
    this._registerExcelFile = httpsCallable(
      this._functions,
      'registerExcelFile'
    );
    this._getExcelFiles = httpsCallable(this._functions, 'getExcelFiles');
    this._getExcelFileById = httpsCallable(this._functions, 'getExcelFileById');
    this._deleteExcelFile = httpsCallable(this._functions, 'deleteExcelFile');
    this._modifyExcelFile = httpsCallable(this._functions, 'modifyExcelFile');
  }

  setFile(id: string, file: Blob): void {
    this.fileMap.set(id, file);
  }

  getFile(id: string): Blob | undefined {
    return this.fileMap.get(id);
  }

  registerExcelFile(
    modelName: string,
    modelVersion: string,
    file: File
  ): Observable<any> {
    const uniqueFileName = this._generateUniqueFileName(
      modelName,
      modelVersion,
      file
    );
    const filePath = `excelFiles/${uniqueFileName}`;

    return from(
      runInInjectionContext(this._injector, () => {
        const fileRef = ref(this._storage, filePath);
        return uploadBytes(fileRef, file).then(() => fileRef);
      })
    ).pipe(
      switchMap((fileRef) =>
        from(
          runInInjectionContext(this._injector, () => getDownloadURL(fileRef))
        ).pipe(
          switchMap((downloadUrl) =>
            from(
              runInInjectionContext(this._injector, () =>
                this._registerExcelFile({
                  modelName: modelName,
                  modelVersion: modelVersion,
                  fileName: uniqueFileName,
                  downloadUrl: downloadUrl,
                })
              )
            )
          )
        )
      )
    );
  }

  getExcelFiles() {
    return from(this._getExcelFiles()) as Observable<
      HttpsCallableResult<FileMetadata[]>
    >;
  }

  getExcelFileById(fileId: string): Observable<FileMetadata | undefined> {
    const tryOnline$ = from(
      this._getExcelFileById(fileId) as Promise<HttpsCallableResult<FileMetadata>>
    );

    return tryOnline$.pipe(
      switchMap((result) => {
        const metadata = result.data;

        if (Object.keys(metadata).length === 0) {
          return of(undefined); // Stop the observable chain
        }

        return this._loadFromCacheOrDownload(fileId, metadata);
      }),
      catchError(() => {
        return from(this._excelCache.get(fileId)).pipe(
          switchMap((cached) => {
            if (!cached) {
              return of(undefined);
            }
            const blob = new Blob([cached.data]);
            this.setFile(fileId, blob);
            return of(cached.metadata);
          })
        );
      })
    );
  }

  deleteExcelFile(fileId: string): Observable<HttpsCallableResult<any>> {
    return from(this._getExcelFileById(fileId)).pipe(
      switchMap((result) => {
        const metadata = result.data as FileMetadata;
        const filePath = 'excelFiles/' + metadata.fileName;
        const fileRef = runInInjectionContext(this._injector, () =>
          ref(this._storage, filePath)
        );

        // First delete the Firestore document
        return from(this._deleteExcelFile({ id: fileId })).pipe(
          switchMap((deleteResult) =>
            // Then delete the file from Storage
            from(
              runInInjectionContext(this._injector, () => deleteObject(fileRef))
            ).pipe(
              switchMap(() => from(this._excelCache.remove(fileId))),
              map(() => deleteResult) // return Firestore deletion result
            )
          )
        );
      })
    );
  }

  modifyExcelFile(
    fileId: string,
    modelVersion: string, // user-provided string version
    file: File
  ): Observable<HttpsCallableResult<any>> {
    return (
      from(this._getExcelFileById(fileId)) as Observable<
        HttpsCallableResult<FileMetadata | Record<string, never>>
      >
    ).pipe(
      switchMap((result) => {
        const metadata = result.data as FileMetadata | undefined;

        if (!metadata || Object.keys(metadata).length === 0) {
          throw new Error('File not found');
        }

        // Build unique filename with the provided version (string)
        const uniqueFileName = this._generateUniqueFileName(
          metadata.modelName,
          modelVersion,
          file
        );
        const filePath = `excelFiles/${uniqueFileName}`;

        // Upload to Storage
        return from(
          runInInjectionContext(this._injector, () => {
            const fileRef = ref(this._storage, filePath);
            return uploadBytes(fileRef, file).then(() => fileRef);
          })
        ).pipe(
          // Get Download URL
          switchMap((fileRef) =>
            from(
              runInInjectionContext(this._injector, () =>
                getDownloadURL(fileRef)
              )
            ).pipe(
              // Call the backend modify function with the manual version
              switchMap((downloadUrl) =>
                from(
                  runInInjectionContext(this._injector, () =>
                    this._modifyExcelFile({
                      id: fileId,
                      fileName: uniqueFileName,
                      downloadUrl,
                      modelVersion, // send as string
                    })
                  )
                ).pipe(
                  switchMap((modifyResult) =>
                    from(file.arrayBuffer()).pipe(
                      tap((buffer) => {
                        const updatedMetadata: FileMetadata = {
                          ...(metadata as FileMetadata),
                          fileName: uniqueFileName,
                          downloadUrl,
                          modelVersion,
                          version: modelVersion,
                        };
                        this._excelCache.set(fileId, updatedMetadata, buffer);
                      }),
                      tap(() => this.setFile(fileId, file)),
                      map(() => modifyResult)
                    )
                  )
                )
              )
            )
          )
        );
      })
    );
  }

  private _generateUniqueFileName(
    modelName: string,
    version: string,
    file: File
  ) {
    return `${modelName}__v${version}__${file.lastModified}__${
      file.size
    }__${this._saltService.generateSalt()}.xlsx`;
  }

  private _loadFromCacheOrDownload(
    fileId: string,
    metadata: FileMetadata
  ): Observable<FileMetadata> {
    return from(this._excelCache.get(fileId)).pipe(
      switchMap((cached) => {
        if (cached && cached.metadata.version === metadata.version) {
          const blob = new Blob([cached.data]);
          this.setFile(fileId, blob);
          return of(cached.metadata);
        }

        const filePath = 'excelFiles/' + metadata.fileName;
        return from(
          runInInjectionContext(this._injector, () => {
            const fileRef = ref(this._storage, filePath);
            return getBytes(fileRef);
          })
        ).pipe(
          tap((bytes) => this._excelCache.set(fileId, metadata, bytes)),
          map((bytes) => new Blob([bytes])),
          tap((blob) => this.setFile(fileId, blob)),
          map(() => metadata)
        );
      })
    );
  }
}
